import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface HeroComic {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  coverImageLarge: string;
  tags: string[];
  rating: number;
  status: string;
}

const heroComics: HeroComic[] = [
  {
    id: 6,
    title: '全职猎魔师',
    subtitle: '2026年度最热韩漫',
    description: 'E级猎魔师意外获得神级力量，从此踏上逆袭之路',
    coverImageLarge: 'https://readdy.ai/api/search-image?query=Epic%20Korean%20manhwa%20hunter%20warrior%20with%20glowing%20weapon%20in%20dramatic%20battle%20pose%2C%20intense%20red%20and%20black%20aesthetic%2C%20dark%20atmospheric%20background%20with%20magical%20energy%20particles%2C%20cinematic%20lighting%2C%20dynamic%20composition%2C%20heroic%20fantasy%20art%20style&width=1600&height=900&seq=hero-bg-01&orientation=landscape',
    tags: ['热血', '战斗', '奇幻'],
    rating: 9.7,
    status: '连载中',
  },
  {
    id: 5,
    title: '女神降临',
    subtitle: '人气霸榜韩漫',
    description: '被霸凌女孩华丽变身，开启全新校园人生',
    coverImageLarge: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20beauty%20transformation%20dramatic%20scene%2C%20young%20woman%20in%20elegant%20dress%20with%20confident%20expression%2C%20soft%20rose%20gold%20and%20pearl%20lighting%2C%20glamorous%20atmosphere%2C%20modern%20city%20night%20background%20with%20sparkles%2C%20fashion%20editorial%20style&width=1600&height=900&seq=hero-bg-02&orientation=landscape',
    tags: ['校园', '成长', '爱情'],
    rating: 9.2,
    status: '连载中',
  },
  {
    id: 11,
    title: '暴君的游戏',
    subtitle: '奇幻权谋巨作',
    description: '最强战士与暴君皇帝的致命交易',
    coverImageLarge: 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20fantasy%20throne%20room%20with%20imposing%20emperor%2C%20rich%20crimson%20and%20gold%20aesthetic%2C%20grand%20palace%20interior%20with%20towering%20pillars%2C%20dramatic%20lighting%20from%20above%2C%20majestic%20and%20powerful%20atmosphere%2C%20cinematic%20composition&width=1600&height=900&seq=hero-bg-03&orientation=landscape',
    tags: ['宫廷', '权谋', '奇幻'],
    rating: 9.6,
    status: '连载中',
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % heroComics.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const comic = heroComics[current];

  return (
    <section className="relative h-[420px] md:h-[600px] overflow-hidden">
      {heroComics.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.coverImageLarge}
            alt={item.title}
            title={`${item.title} - 韩漫大全`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className={`transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <span className="inline-block px-4 py-1 rounded-full border border-background-50/30 text-background-50 text-xs mb-4 backdrop-blur-sm">
                {comic.subtitle}
              </span>
            </div>
            <h1 className={`font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-4 leading-tight transition-all duration-500 delay-75 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {comic.title}
            </h1>
            <p className={`text-sm md:text-base text-background-200 mb-6 max-w-lg mx-auto transition-all duration-500 delay-150 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {comic.description}
            </p>
            <div className={`flex items-center justify-center gap-3 mb-6 flex-wrap transition-all duration-500 delay-200 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <span className="text-accent-400 text-sm font-medium">{comic.rating}分</span>
              <span className="text-background-300 text-sm">·</span>
              <span className="text-background-300 text-sm">{comic.status}</span>
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-background-50/15 text-background-200 text-xs backdrop-blur-sm whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <div className={`flex items-center justify-center gap-4 flex-col sm:flex-row transition-all duration-500 delay-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <Link
                to={`/comic/${comic.id}`}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                立即阅读
              </Link>
              <Link
                to="/category/completed"
                className="px-8 py-3 border border-background-50/30 text-background-50 rounded-full text-sm hover:bg-background-50/10 transition-colors duration-200 whitespace-nowrap"
              >
                探索更多
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {heroComics.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === current
                ? 'bg-background-50 w-6'
                : 'bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + heroComics.length) % heroComics.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors duration-200 backdrop-blur-sm"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors duration-200 backdrop-blur-sm"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>
    </section>
  );
}
